import { useState, useCallback } from 'react';
import { auth } from '../../../../firebase';
import { SERVICE_ENDPOINTS, SERVICE_CONFIG, HEALTH_CHECK_ENDPOINTS, API_PATHS } from '../constants/serviceEndpoints';

export const useServiceTesting = () => {
  const [testHistory, setTestHistory] = useState([]);

  const getAuthHeaders = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      console.warn('No authenticated user found for service testing');
      return {};
    }

    try {
      // Get Firebase ID token for Flask app authentication
      const token = await user.getIdToken();
      return {
        'Authorization': `Bearer ${token}`
      };
    } catch (error) {
      console.error('Error getting auth token:', error);
      return {};
    }
  }, []);

  const getServiceConfig = useCallback((service) => {
    return SERVICE_CONFIG[service] || null;
  }, []);

  const formatServiceResponse = useCallback((response) => {
    // Format the response for better display
    try {
      return typeof response === 'string' ? JSON.parse(response) : response;
    } catch (error) {
      return response;
    }
  }, []);

  const checkServiceHealth = useCallback(async (service, environment = 'development') => {
    let healthEndpoint;
    
    // Handle environment-specific endpoints
    if (environment && environment !== 'default') {
      if (HEALTH_CHECK_ENDPOINTS[environment.toUpperCase()] && HEALTH_CHECK_ENDPOINTS[environment.toUpperCase()][service]) {
        healthEndpoint = HEALTH_CHECK_ENDPOINTS[environment.toUpperCase()][service];
      } else {
        // Use the SERVICE_IN_ENVIRONMENT function for dynamic endpoints
        healthEndpoint = HEALTH_CHECK_ENDPOINTS.SERVICE_IN_ENVIRONMENT(environment, service.toLowerCase());
      }
    } else {
      // Use backward compatible endpoints
      healthEndpoint = HEALTH_CHECK_ENDPOINTS[service];
    }
    
    if (!healthEndpoint) {
      throw new Error(`No health check endpoint configured for ${service} in ${environment} environment`);
    }

    console.log(`🔍 Checking health for ${service} in ${environment} at ${healthEndpoint}`);
    const startTime = Date.now();
    
    try {
      // Health checks through Flask API gateway (same origin - no CORS issues)
      const response = await fetch(healthEndpoint, {
        method: 'GET',
        credentials: 'include', // Include cookies for Flask session
        // No additional headers needed for same-origin requests
        signal: AbortSignal.timeout(10000) // 10 second timeout
      });

      const responseTime = Date.now() - startTime;
      console.log(`✅ Health check response for ${service}:`, {
        status: response.status,
        ok: response.ok,
        responseTime: responseTime + 'ms',
        headers: Object.fromEntries(response.headers.entries())
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Health check failed for ${service}:`, {
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        let errorMessage = `Health check failed: ${response.status} ${response.statusText}`;
        if (errorText) {
          errorMessage += ` - ${errorText}`;
        }
        throw new Error(errorMessage);
      }

      const healthData = await response.json();
      console.log(`📊 Health data for ${service}:`, healthData);
      
      // Determine the actual status from the service response
      let actualStatus = 'unknown';
      if (response.ok) {
        // If the service returns a status field, use it; otherwise default to 'healthy'
        actualStatus = healthData.status || 'healthy';
      } else {
        actualStatus = 'unhealthy';
      }
      
      console.log(`🎯 Final status for ${service}: ${actualStatus}`);
      
      return {
        status: actualStatus,
        response_time: responseTime,
        url: healthEndpoint,
        service: service,
        timestamp: new Date().toISOString(),
        ...healthData
      };
    } catch (error) {
      const responseTime = Date.now() - startTime;
      console.error(`💥 Health check error for ${service}:`, {
        error: error.message,
        name: error.name,
        responseTime: responseTime + 'ms',
        stack: error.stack
      });
      
      return {
        status: 'unhealthy',
        message: error.message,
        response_time: responseTime,
        url: healthEndpoint,
        service: service,
        timestamp: new Date().toISOString(),
        error: error.name === 'TypeError' ? 'Network error - check if Flask app is running' : error.message
      };
    }
  }, []); // No auth dependency since same-origin requests don't need special auth

  const checkAllEnvironmentsHealth = useCallback(async () => {
    try {
      console.log('🔍 Checking health across all environments');
      const response = await fetch(HEALTH_CHECK_ENDPOINTS.ALL_ENVIRONMENTS, {
        method: 'GET',
        credentials: 'include',
        signal: AbortSignal.timeout(30000) // 30 second timeout for all environments
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Multi-environment health check failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const healthData = await response.json();
      console.log('📊 Multi-environment health data:', healthData);
      
      return healthData;
    } catch (error) {
      console.error('❌ Multi-environment health check failed:', error);
      throw error;
    }
  }, []);

  const checkEnvironmentHealth = useCallback(async (environment) => {
    try {
      console.log(`🔍 Checking health for ${environment} environment`);
      const endpoint = HEALTH_CHECK_ENDPOINTS.ENVIRONMENT(environment);
      const response = await fetch(endpoint, {
        method: 'GET',
        credentials: 'include',
        signal: AbortSignal.timeout(20000) // 20 second timeout
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`${environment} environment health check failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const healthData = await response.json();
      console.log(`📊 ${environment} environment health data:`, healthData);
      
      return healthData;
    } catch (error) {
      console.error(`❌ ${environment} environment health check failed:`, error);
      throw error;
    }
  }, []);

  const formatServiceRequest = useCallback((service, inputs) => {
    switch (service) {
      case 'GEOCODING':
        // Format for geocoding service API
        const fullAddress = [
          inputs.address,
          inputs.city,
          inputs.state,
          inputs.zip,
          inputs.country
        ].filter(Boolean).join(', ');
        
        return {
          addresses: [fullAddress]
        };
      
      case 'ESRI':
        // Format for ESRI service API - only latitude and longitude required
        return {
          latitude: parseFloat(inputs.latitude),
          longitude: parseFloat(inputs.longitude)
        };
      
      case 'NEARBY_SCHOOLS':
        // Format for nearby schools test API
        return {
          school_uuid: inputs.school_uuid,
          drive_times: inputs.drive_times ? 
            inputs.drive_times.split(',').map(t => parseInt(t.trim())) : 
            [5, 10, 15],
          data_years: inputs.data_years ? 
            inputs.data_years.split(',').map(y => parseInt(y.trim())) : 
            [2019, 2023]
        };
      
      case 'PROJECTIONS':
        // Format for backend projections test API
        const projectionRequest = {
          school_uuid: inputs.school_uuid,
          projection_years: parseInt(inputs.projection_years || 5)
        };
        
        if (inputs.enrollment_data) {
          projectionRequest.enrollment_data = inputs.enrollment_data;
        }
        
        return projectionRequest;
      
      case 'METRICS':
        // Format for metrics test API
        return {
          school_uuid: inputs.school_uuid,
          drive_times: inputs.drive_times ? 
            inputs.drive_times.split(',').map(t => parseInt(t.trim())) : 
            [5, 10, 15],
          data_years: inputs.data_years ? 
            inputs.data_years.split(',').map(y => parseInt(y.trim())) : 
            [2019, 2023]
        };
      
      case 'SPLITS':
        // Format for splits test API
        return {
          identifier: inputs.parent_ncessch,
          split_config: inputs.split_config || {},
          addresses: inputs.addresses || {}
        };
      
      default:
        return inputs;
    }
  }, []);

  const testService = useCallback(async (service, inputs) => {
    const serviceEndpoint = SERVICE_ENDPOINTS[service];
    if (!serviceEndpoint) {
      throw new Error(`No endpoint configured for ${service}`);
    }

    const serviceConfig = SERVICE_CONFIG[service];
    
    // Use TEST endpoint for services that support test mode, otherwise use PROCESS
    const apiPath = serviceConfig?.test_mode_supported ? 
      API_PATHS[service]?.TEST : 
      API_PATHS[service]?.PROCESS || '/process';
    
    const url = `${serviceEndpoint}${apiPath}`;

    // Format the request body based on service type for Flask API gateway
    const requestBody = formatServiceRequest(service, inputs);

    // Get Firebase auth headers for Flask app
    const authHeaders = await getAuthHeaders();

    // Create timeout controller for better browser compatibility
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, serviceConfig?.timeout || 30000);

    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders
      },
      credentials: 'include', // Include cookies for Flask session
      body: JSON.stringify(requestBody),
      signal: controller.signal
    };

    try {
      console.log(`🔄 Making request to ${url}`, { method: requestOptions.method, timeout: serviceConfig?.timeout });
      const response = await fetch(url, requestOptions);
      
      // Clear the timeout on successful response
      clearTimeout(timeoutId);
      
      const contentLength = response.headers.get('content-length');
      console.log(`📥 Response received:`, { 
        status: response.status, 
        ok: response.ok, 
        contentType: response.headers.get('content-type'),
        contentLength: contentLength ? `${contentLength} bytes (~${Math.round(contentLength/1024)}KB)` : 'unknown'
      });
      
      // Warn about large responses
      if (contentLength && parseInt(contentLength) > 50000) {
        console.warn(`⚠️ Large response detected (${Math.round(contentLength/1024)}KB). This may cause parsing delays.`);
      }
      
      if (!response.ok) {
        let errorMessage = `Service error: ${response.status} ${response.statusText}`;
        
        if (response.status === 401) {
          errorMessage = 'Authentication required - please log in to the Flask app';
        } else if (response.status === 403) {
          errorMessage = 'Insufficient permissions - check Flask app authorization';
        } else {
          try {
            const errorData = await response.json();
            if (errorData.error) {
              errorMessage += ` - ${errorData.error}`;
            }
          } catch (e) {
            // If can't parse error response, use default message
          }
        }
        
        throw new Error(errorMessage);
      }

      console.log(`🔍 Parsing JSON response...`);
      const responseData = await response.json();
      console.log(`✅ JSON parsed successfully, response size:`, JSON.stringify(responseData).length, 'characters');
      
      // Add to test history
      const testResult = {
        service,
        inputs,
        requestBody,
        response: responseData,
        timestamp: new Date().toISOString(),
        success: true,
        url
      };
      
      setTestHistory(prev => [testResult, ...prev.slice(0, 49)]); // Keep last 50 tests
      
      return responseData;
    } catch (error) {
      // Clear the timeout on error
      clearTimeout(timeoutId);
      
      console.error(`❌ Service test failed:`, {
        service,
        url,
        error: error.message,
        errorType: error.name,
        stack: error.stack
      });
      
      // Enhanced error message based on error type
      let enhancedErrorMessage = error.message;
      if (error.name === 'AbortError') {
        enhancedErrorMessage = `Request timed out after ${serviceConfig?.timeout || 30000}ms - the service may be returning a very large response`;
      } else if (error.message.includes('Failed to fetch')) {
        enhancedErrorMessage = 'Network error or CORS issue - check browser console for details';
      } else if (error.message.includes('JSON')) {
        enhancedErrorMessage = 'JSON parsing error - the service may have returned invalid JSON or HTML instead of JSON';
      }
      
      // Add error to test history
      const testResult = {
        service,
        inputs,
        requestBody: formatServiceRequest(service, inputs),
        error: enhancedErrorMessage,
        originalError: error.message,
        timestamp: new Date().toISOString(),
        success: false,
        url
      };
      
      setTestHistory(prev => [testResult, ...prev.slice(0, 49)]); // Keep last 50 tests
      
      // Throw enhanced error
      const enhancedError = new Error(enhancedErrorMessage);
      enhancedError.originalError = error;
      throw enhancedError;
    }
  }, [formatServiceRequest, getAuthHeaders]);

  const testServiceBatch = useCallback(async (service, inputsArray) => {
    const serviceEndpoint = SERVICE_ENDPOINTS[service];
    if (!serviceEndpoint) {
      throw new Error(`No endpoint configured for ${service}`);
    }

    const serviceConfig = SERVICE_CONFIG[service];
    const apiPath = API_PATHS[service]?.BATCH || '/batch';
    const url = `${serviceEndpoint}${apiPath}`;

    // Get authentication headers
    const headers = await getAuthHeaders();
    if (!headers['Authorization']) {
      throw new Error('Authentication required - please log in');
    }

    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...headers
      },
      body: JSON.stringify({ inputs: inputsArray }),
      // Add timeout based on service config with extra time for batch
      signal: AbortSignal.timeout((serviceConfig?.timeout || 30000) * 2)
    };

    try {
      const response = await fetch(url, requestOptions);
      
      if (!response.ok) {
        let errorMessage = `Batch service error: ${response.status} ${response.statusText}`;
        
        if (response.status === 401) {
          errorMessage = 'Authentication required - token may be expired';
        } else if (response.status === 403) {
          errorMessage = 'Invalid or expired token - insufficient permissions';
        } else {
          try {
            const errorData = await response.json();
            if (errorData.error) {
              errorMessage += ` - ${errorData.error}`;
            }
          } catch (e) {
            // If can't parse error response, use default message
          }
        }
        
        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      
      // Add to test history
      const testResult = {
        service,
        inputs: inputsArray,
        response: responseData,
        timestamp: new Date().toISOString(),
        success: true,
        url,
        type: 'batch'
      };
      
      setTestHistory(prev => [testResult, ...prev.slice(0, 49)]);
      
      return responseData;
    } catch (error) {
      // Add error to test history
      const testResult = {
        service,
        inputs: inputsArray,
        error: error.message,
        timestamp: new Date().toISOString(),
        success: false,
        url,
        type: 'batch'
      };
      
      setTestHistory(prev => [testResult, ...prev.slice(0, 49)]);
      
      throw error;
    }
  }, [getAuthHeaders]);

  const validateServiceInputs = useCallback((service, inputs) => {
    const validationRules = {
      GEOCODING: (inputs) => {
        if (!inputs.address && !inputs.city) {
          return 'Address or city is required for geocoding';
        }
        return null;
      },
      NCES: (inputs) => {
        if (!inputs.name || !inputs.state_abbr) {
          return 'School name and state abbreviation are required';
        }
        // Validate state abbreviation format
        if (inputs.state_abbr && inputs.state_abbr.length !== 2) {
          return 'State abbreviation must be exactly 2 characters';
        }
        // Validate NCESSCH format if provided
        if (inputs.ncessch && (!/^\d{12}$/.test(inputs.ncessch))) {
          return 'NCESSCH must be a 12-digit number if provided';
        }
        return null;
      },
      ESRI: (inputs) => {
        if (!inputs.latitude || !inputs.longitude) {
          return 'Latitude and longitude are required for ESRI test';
        }
        // Basic coordinate validation
        const lat = parseFloat(inputs.latitude);
        const lng = parseFloat(inputs.longitude);
        if (isNaN(lat) || lat < -90 || lat > 90) {
          return 'Latitude must be a number between -90 and 90';
        }
        if (isNaN(lng) || lng < -180 || lng > 180) {
          return 'Longitude must be a number between -180 and 180';
        }
        return null;
      },
      NEARBY_SCHOOLS: (inputs) => {
        if (!inputs.school_uuid) {
          return 'School UUID is required for nearby schools test';
        }
        // Basic UUID format validation (loose check)
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!uuidPattern.test(inputs.school_uuid)) {
          return 'Please enter a valid UUID format';
        }
        return null;
      },
      PROJECTIONS: (inputs) => {
        if (!inputs.school_uuid) {
          return 'School UUID is required for projections test';
        }
        // Basic UUID format validation (loose check)
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!uuidPattern.test(inputs.school_uuid)) {
          return 'Please enter a valid UUID format';
        }
        return null;
      },
      METRICS: (inputs) => {
        if (!inputs.school_uuid) {
          return 'School UUID is required for metrics test';
        }
        // Basic UUID format validation (loose check)
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!uuidPattern.test(inputs.school_uuid)) {
          return 'Please enter a valid UUID format';
        }
        return null;
      },
      TEAM: (inputs) => {
        if (!inputs.name) {
          return 'Team name is required';
        }
        // Validate school UUIDs if provided
        if (inputs.school_uuids && inputs.school_uuids.length > 0) {
          const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          for (const uuid of inputs.school_uuids) {
            if (!uuidPattern.test(uuid)) {
              return `Invalid UUID format: ${uuid}`;
            }
          }
        }
        return null;
      },
      SPLITS: (inputs) => {
        if (!inputs.parent_ncessch) {
          return 'Parent School NCESSCH is required for splits testing';
        }
        // Basic NCESSCH format validation (loose check)
        if (!/^\d{12}$/.test(inputs.parent_ncessch)) {
          return 'NCESSCH must be a 12-digit number';
        }
        // Check if split_config is provided and has at least 2 levels
        if (!inputs.split_config || typeof inputs.split_config !== 'object') {
          return 'Split configuration is required';
        }
        const levels = Object.keys(inputs.split_config).filter(level => 
          ['elementary', 'middle', 'high'].includes(level) && 
          inputs.split_config[level] && 
          inputs.split_config[level].gradesServed && 
          inputs.split_config[level].gradesServed.length > 0
        );
        if (levels.length < 2) {
          return 'At least 2 school levels must be configured for split testing';
        }
        return null;
      }
    };

    const validator = validationRules[service];
    if (validator) {
      return validator(inputs);
    }
    return null;
  }, []);

  const clearTestHistory = useCallback(() => {
    setTestHistory([]);
  }, []);

  const getTestHistory = useCallback(() => {
    return testHistory;
  }, [testHistory]);

  const exportTestHistory = useCallback(() => {
    const dataStr = JSON.stringify(testHistory, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `service_test_history_${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  }, [testHistory]);

  return {
    testService,
    testServiceBatch,
    checkServiceHealth,
    checkAllEnvironmentsHealth,
    checkEnvironmentHealth,
    validateServiceInputs,
    formatServiceResponse,
    getServiceConfig,
    testHistory,
    clearTestHistory,
    getTestHistory,
    exportTestHistory
  };
}; 